import type { Session } from '@setplex/tria-api'
import type {
  DataEvent,
  SystemEvent,
  SystemEventClose,
  SystemEventConnected,
  SystemEventPing,
  SystemEventReconnect,
} from './schemas'

import {
  attach,
  combine,
  createEvent,
  createStore,
  restore,
  sample,
  scopeBind,
  split,
} from 'effector'
import { persist } from 'effector-storage/session'
import { EventSource } from 'eventsource'
import * as v from 'valibot'
import { runtime } from '~/app/environment'
import { model as config, remote } from '~/shared/config'
import { holdup, noop } from '~/shared/effector'
import {
  CONNECTED_TIMEOUT,
  logger,
  SSE_PING_TIMEOUT,
  typeOf,
  variantOf,
} from './lib'
import { AnyEvent, AnyEventVariant, SystemEventType } from './schemas'

export const init = createEvent()

export const session = createEvent<Session | null>()
const connect = createEvent()
const close = createEvent()

export const message = createEvent<AnyEvent>()
const error = createEvent<unknown>()

const $disabled = config.get(remote.tria_isServerSentEventsDisabled)

const $session = restore(session, null)
const $deviceIdentifier = $session.map(
  (session) => session?.deviceIdentifier || null
)

split({
  source: combine([$deviceIdentifier, $disabled]),
  match: ([device, disabled]) => (device && !disabled ? 'connect' : 'close'),
  cases: { connect, close },
})

/*
 * prefiltered events' payloads
 */

const { systemEvent, dataEvent } = split(message, {
  systemEvent: variantOf<SystemEvent>(AnyEventVariant.system),
  dataEvent: variantOf<DataEvent>(AnyEventVariant.data),
})

export const systemMessage = systemEvent.map((event) => event.data)
export const dataMessage = dataEvent.map((event) => event.data)

const $lastEventId = createStore<string | null>(null) //
  .on(dataEvent, (_, event) => event.id)

// we do not use scopes for now, so, ignore this rule
// eslint-disable-next-line effector/require-pickup-in-persist
persist({
  store: $lastEventId,
  key: 'tria__last-event-id',
  fail: noop,
})

/*
 * manage sse connection and subscriptions
 */

const $eventSource = createStore<EventSource | null>(null)

// open connection
const openFx = attach({
  source: [$deviceIdentifier, $lastEventId],
  async effect([deviceIdentifier, lastEventId]) {
    await closeFx()

    const url =
      (globalThis.__ERA_WBS_PROXY__ || '') +
      `/wbs/web/api/v3/devices/${deviceIdentifier}/messages`
    return new EventSource(url, {
      fetch(input, init) {
        const headers = init?.headers || {}
        if (runtime.app.organizationId) {
          headers['multiorg-id'] = String(runtime.app.organizationId)
        }
        if (lastEventId) {
          headers['last-event-id'] = lastEventId
        }
        return fetch(input, { ...init, headers })
      },
    })
  },
})

// close connection
const closeFx = attach({
  source: $eventSource,
  effect(eventSource) {
    if (eventSource != null && eventSource.readyState !== EventSource.CLOSED) {
      eventSource.close()
    }
  },
})

// subscribe to errors and messages
const subscribeFx = attach({
  source: $eventSource,
  effect(eventSource) {
    if (eventSource == null) {
      throw new Error('SSE is not ready')
    }

    const errorReceived = scopeBind(error, { safe: true })
    eventSource.addEventListener('error', errorReceived)

    const messageReceived = scopeBind(message, { safe: true })
    for (const type of Object.values(AnyEventVariant)) {
      eventSource.addEventListener(type, (ev) => {
        try {
          messageReceived(
            v.parse(AnyEvent, {
              id: ev.lastEventId,
              event: type,
              data: JSON.parse(ev.data),
            })
          )
        } catch (e) {
          errorReceived({
            message: `invalid "${type}" event data: ${ev.data}`,
            error: e,
          })
        }
      })
    }
  },
})

/// open connection

sample({
  clock: connect,
  target: openFx,
})

sample({
  clock: openFx.doneData,
  target: $eventSource,
})

sample({
  clock: connect,
  fn: () => ['connecting...'],
  target: logger.traceFx,
})

sample({
  clock: openFx.done,
  fn: () => ['successfully connected'],
  target: logger.traceFx,
})

sample({
  clock: openFx.fail,
  fn: (fail) => ['could not open connection:', fail],
  target: logger.errorFx,
})

/// close connection

sample({
  clock: close,
  target: closeFx,
})

sample({
  clock: [closeFx.finally, openFx.fail],
  fn: () => null,
  target: $eventSource,
})

sample({
  clock: close,
  fn: () => ['closing connection...'],
  target: logger.traceFx,
})

sample({
  clock: closeFx.fail,
  fn: (fail) => ['could not close connection:', fail],
  target: logger.errorFx,
})

/// manage subscriptions and unsubscriptions

sample({
  clock: $eventSource,
  filter: Boolean,
  target: subscribeFx,
})

sample({
  clock: subscribeFx.fail,
  fn: (fail) => ['could not subscribe to events:', fail],
  target: logger.errorFx,
})

sample({
  clock: error,
  fn: (fail) => ['error:', fail],
  target: logger.errorFx,
})

/*
 * handle system messages
 */

const {
  connectedSystemEvent,
  pingSystemEvent,
  reconnectSystemEvent,
  closeSystemEvent,
} = split(systemMessage, {
  connectedSystemEvent: typeOf<SystemEventConnected>(SystemEventType.connected),
  pingSystemEvent: typeOf<SystemEventPing>(SystemEventType.ping),
  reconnectSystemEvent: typeOf<SystemEventReconnect>(SystemEventType.reconnect),
  closeSystemEvent: typeOf<SystemEventClose>(SystemEventType.close),
})

sample({
  clock: connectedSystemEvent,
  fn: (message) => ['connection established:', message],
  target: logger.traceFx,
})

sample({
  clock: pingSystemEvent,
  fn: (message) => ['ping received:', message],
  target: logger.traceFx,
})

sample({
  clock: reconnectSystemEvent,
  fn: (message) => ['reconnect requested by server:', message],
  target: logger.traceFx,
})

sample({
  clock: closeSystemEvent,
  fn: (message) => ['close connection requested by server:', message],
  target: logger.traceFx,
})

sample({
  clock: dataMessage,
  fn: (message) => ['data message received:', message],
  target: logger.traceFx,
})

/// close connection on close event

sample({
  clock: closeSystemEvent,
  target: close,
})

/// reconnect on RECONNECT event

const reconnect = createEvent()

sample({
  clock: reconnect,
  fn: () => ['reconnecting after RECONNECT event...'],
  target: logger.traceFx,
})

sample({
  clock: reconnect,
  target: connect,
})

const delayedReconnect = holdup({
  target: reconnect,
  cancel: [close, connect],
})

sample({
  clock: reconnectSystemEvent,
  fn: (msg) => msg.retry || 0,
  target: [close, delayedReconnect],
})

/// reconnect if no CONNECTED event received in 1 minute

const reconnectBecauseNoConnected = createEvent()

sample({
  clock: reconnectBecauseNoConnected,
  fn: () => ['reconnecting because no CONNECTED event received in 1 minute...'],
  target: logger.warnFx,
})

sample({
  clock: reconnectBecauseNoConnected,
  target: connect,
})

const delayedReconnectBecauseNoConnected = holdup({
  target: connect,
  cancel: [close, connect, connectedSystemEvent],
})

sample({
  clock: connect,
  filter: config.get(remote.tria_isServerSentEventsReconnectionEnabled),
  fn: () => CONNECTED_TIMEOUT,
  target: delayedReconnectBecauseNoConnected,
})

/// reconnect if no PING event received in 5 minutes

const retryConnectNoPing = createEvent()

sample({
  clock: retryConnectNoPing,
  fn: () => ['reconnecting because no PING event received in 5 minutes...'],
  target: logger.warnFx,
})

sample({
  clock: retryConnectNoPing,
  target: connect,
})

const delayedRetryConnectNoPing = holdup({
  target: connect,
  cancel: [close, connect, pingSystemEvent],
})

sample({
  clock: pingSystemEvent,
  filter: config.get(remote.tria_isServerSentEventsReconnectionEnabled),
  fn: () => SSE_PING_TIMEOUT,
  target: delayedRetryConnectNoPing,
})
